/**
 * The chainable query builder.
 *
 * Every method returns `this`, so both `q.eq(...).order(...)` and the
 * re-assigning style (`q = q.eq(...)`) work. The builder is a real `PromiseLike`
 * — several call sites `.then()` it directly or drop an un-awaited builder into
 * `Promise.all([...])` — and it executes at most once, so awaiting an insert
 * twice cannot double-write.
 */
import { matchAll, parseOrFilter, type CmpOp, type FilterNode, cmp } from './filters';
import { parseSelect, type ParsedSelect } from './select';
import { projectRows } from './embeds';
import type { DemoStore } from './store';
import { OK_STATUS, OK_STATUS_TEXT, type FakeResult, type Row } from './types';
import { compareValues, isNullish } from './values';

type OrderSpec = { col: string; ascending: boolean; nullsFirst: boolean };
type Mode = 'select' | 'insert' | 'update' | 'delete';

function newId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function ok(data: unknown, count: number | null = null): FakeResult {
  return { data, error: null, count, status: OK_STATUS, statusText: OK_STATUS_TEXT };
}

/** Stable multi-key sort: ties fall back to the original row order. */
function sortRows(rows: Row[], specs: OrderSpec[]): number[] {
  const idx = rows.map((_, i) => i);
  if (specs.length === 0) return idx;
  idx.sort((ia, ib) => {
    for (const s of specs) {
      const a = rows[ia]![s.col];
      const b = rows[ib]![s.col];
      const an = isNullish(a);
      const bn = isNullish(b);
      if (an && bn) continue;
      if (an || bn) {
        const nullFirst = s.nullsFirst ? -1 : 1;
        return an ? nullFirst : -nullFirst;
      }
      const c = compareValues(a, b);
      if (c !== 0) return s.ascending ? c : -c;
    }
    return ia - ib;
  });
  return idx;
}

export class FakeQuery implements PromiseLike<FakeResult> {
  private mode: Mode = 'select';

  private columns: string | undefined;

  private countMode: string | null = null;

  private head = false;

  private returning = false;

  private readonly filters: FilterNode[] = [];

  private readonly orders: OrderSpec[] = [];

  private limitN: number | null = null;

  private rangeSpec: { from: number; to: number } | null = null;

  private singleRow = false;

  private payload: Row[] = [];

  private forceNullData = false;

  private ran: Promise<FakeResult> | null = null;

  constructor(private readonly store: DemoStore, private readonly table: string) {}

  /** Used by `rpc()`, which has no table behind it. */
  nullResult(): this {
    this.forceNullData = true;
    return this;
  }

  // ── shaping ───────────────────────────────────────────────────────────────

  select(columns?: string, opts?: { count?: string | null; head?: boolean }): this {
    this.columns = columns;
    if (opts?.count) this.countMode = opts.count;
    if (opts?.head) this.head = true;
    // `.insert(...).select()` — unused today, cheap to support.
    if (this.mode !== 'select') this.returning = true;
    return this;
  }

  // ── filters ───────────────────────────────────────────────────────────────

  private push(col: string, op: CmpOp, value: unknown, negate = false): this {
    this.filters.push(cmp(col, op, value, negate));
    return this;
  }

  eq(col: string, value: unknown): this { return this.push(col, 'eq', value); }

  neq(col: string, value: unknown): this { return this.push(col, 'neq', value); }

  gt(col: string, value: unknown): this { return this.push(col, 'gt', value); }

  gte(col: string, value: unknown): this { return this.push(col, 'gte', value); }

  lt(col: string, value: unknown): this { return this.push(col, 'lt', value); }

  lte(col: string, value: unknown): this { return this.push(col, 'lte', value); }

  like(col: string, value: string): this { return this.push(col, 'like', value); }

  ilike(col: string, value: string): this { return this.push(col, 'ilike', value); }

  is(col: string, value: unknown): this { return this.push(col, 'is', value); }

  in(col: string, values: readonly unknown[]): this { return this.push(col, 'in', values as unknown[]); }

  contains(col: string, value: unknown): this { return this.push(col, 'cs', value); }

  overlaps(col: string, value: unknown): this { return this.push(col, 'ov', value); }

  /** Three-arg PostgREST negation: `.not('division', 'eq', 'REF')`. */
  not(col: string, op: string, value: unknown): this {
    return this.push(col, op as CmpOp, value, true);
  }

  filter(col: string, op: string, value: unknown): this {
    if (op.startsWith('not.')) return this.not(col, op.slice(4), value);
    return this.push(col, op as CmpOp, value);
  }

  match(query: Record<string, unknown>): this {
    for (const [k, v] of Object.entries(query)) this.push(k, 'eq', v);
    return this;
  }

  or(filterString: string): this {
    const node = parseOrFilter(filterString);
    if (node) this.filters.push(node);
    return this;
  }

  // ── ordering / paging ─────────────────────────────────────────────────────

  order(col: string, opts?: { ascending?: boolean; nullsFirst?: boolean }): this {
    const ascending = opts?.ascending ?? true;
    // Postgres default: NULLS LAST for ASC, NULLS FIRST for DESC.
    const nullsFirst = opts?.nullsFirst ?? !ascending;
    this.orders.push({ col, ascending, nullsFirst });
    return this;
  }

  limit(n: number): this { this.limitN = n; return this; }

  range(from: number, to: number): this { this.rangeSpec = { from, to }; return this; }

  maybeSingle(): this { this.singleRow = true; return this; }

  /** The app never calls this; behave like `maybeSingle` rather than erroring. */
  single(): this { this.singleRow = true; return this; }

  returns<T>(): this & PromiseLike<{ data: T; error: null; count: number | null }> {
    return this as never;
  }

  throwOnError(): this { return this; }

  abortSignal(_signal?: unknown): this { return this; }

  // ── mutations ─────────────────────────────────────────────────────────────

  insert(values: Row | Row[]): this {
    this.mode = 'insert';
    this.payload = Array.isArray(values) ? values.map((v) => ({ ...v })) : [{ ...values }];
    return this;
  }

  upsert(values: Row | Row[], _opts?: unknown): this {
    return this.insert(values);
  }

  update(values: Row): this {
    this.mode = 'update';
    this.payload = [{ ...values }];
    return this;
  }

  delete(): this {
    this.mode = 'delete';
    return this;
  }

  // ── execution ─────────────────────────────────────────────────────────────

  private async exec(): Promise<FakeResult> {
    if (this.forceNullData) return ok(null);
    const rows = await this.store.table(this.table);
    switch (this.mode) {
      case 'insert': return this.execInsert(rows);
      case 'update': return this.execUpdate(rows);
      case 'delete': return this.execDelete(rows);
      default: return this.execSelect(rows);
    }
  }

  private async execSelect(rows: Row[]): Promise<FakeResult> {
    const sel: ParsedSelect = parseSelect(this.columns);
    const filtered = rows.filter((r) => matchAll(r, this.filters));

    const { out, keep } = await projectRows(this.store, this.table, filtered, sel);
    const base: Row[] = [];
    const projected: Row[] = [];
    for (let i = 0; i < filtered.length; i += 1) {
      if (!keep[i]) continue;
      base.push(filtered[i]!);
      projected.push(out[i]!);
    }

    const order = sortRows(base, this.orders);
    let result = order.map((i) => projected[i]!);

    // `count: 'exact'` is measured after filters but before range/limit.
    const count = this.countMode ? result.length : null;
    if (this.head) return ok([], count);

    if (this.rangeSpec) {
      const { from, to } = this.rangeSpec;
      result = result.slice(Math.max(0, from), to < 0 ? 0 : to + 1);
    }
    if (this.limitN !== null) result = result.slice(0, Math.max(0, this.limitN));

    if (this.singleRow) return ok(result[0] ?? null, count);
    return ok(result, count);
  }

  private async execInsert(rows: Row[]): Promise<FakeResult> {
    const sample = rows[0];
    const now = new Date().toISOString();
    const created = this.payload.map((v) => {
      const rec: Row = { ...v };
      if (rec.id === undefined && (!sample || 'id' in sample)) rec.id = newId();
      for (const stamp of ['created_at', 'updated_at']) {
        if (rec[stamp] === undefined && (!sample || stamp in sample)) rec[stamp] = now;
      }
      return rec;
    });
    rows.push(...created);
    if (!this.returning) return ok(null);
    const { out } = await projectRows(this.store, this.table, created, parseSelect(this.columns));
    return ok(this.singleRow ? out[0] ?? null : out);
  }

  private async execUpdate(rows: Row[]): Promise<FakeResult> {
    const patch = this.payload[0] ?? {};
    const touched: Row[] = [];
    for (const r of rows) {
      if (!matchAll(r, this.filters)) continue;
      Object.assign(r, patch);
      if ('updated_at' in r && patch.updated_at === undefined) r.updated_at = new Date().toISOString();
      touched.push(r);
    }
    if (!this.returning) return ok(null);
    const { out } = await projectRows(this.store, this.table, touched, parseSelect(this.columns));
    return ok(this.singleRow ? out[0] ?? null : out);
  }

  private async execDelete(rows: Row[]): Promise<FakeResult> {
    const removed: Row[] = [];
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      if (!matchAll(rows[i]!, this.filters)) continue;
      removed.push(rows[i]!);
      rows.splice(i, 1);
    }
    removed.reverse();
    if (!this.returning) return ok(null);
    const { out } = await projectRows(this.store, this.table, removed, parseSelect(this.columns));
    return ok(this.singleRow ? out[0] ?? null : out);
  }

  private run(): Promise<FakeResult> {
    if (!this.ran) this.ran = this.exec();
    return this.ran;
  }

  then<TResult1 = FakeResult, TResult2 = never>(
    onfulfilled?: ((value: FakeResult) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
  ): Promise<TResult1 | TResult2> {
    return this.run().then(onfulfilled ?? undefined, onrejected ?? undefined);
  }

  catch<TResult = never>(
    onrejected?: ((reason: unknown) => TResult | PromiseLike<TResult>) | null,
  ): Promise<FakeResult | TResult> {
    return this.run().catch(onrejected ?? undefined);
  }

  finally(onfinally?: (() => void) | null): Promise<FakeResult> {
    return this.run().finally(onfinally ?? undefined);
  }
}
